import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Column } from 'react-table';

import { useSearchParams } from 'react-router-dom';
import { CodeSupplier, IData } from 'models/order';
import { useTranslation } from 'react-i18next';
import DefaultTable from 'components/tables/DefaultTable/DefaultTable';
import useHttp from 'utils/hooks/useHttp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleLeft, faCancel, faCheckCircle, faEdit, faPlus, faSave } from '@fortawesome/free-solid-svg-icons';
import './ClientOrderDetails.scss';
import DefaultButton from 'components/buttons/DefaultButton/DefaultButton';
import Modal from 'components/modal/Modal';
import OrderConfirmationModal from '../OrderConfirmationModal/OrderConfirmationModal';
import { notify } from 'utils/marketplace';
import OrderModal from 'applications/automotor/components/OrderModal/OrderModal';
import { IProformaResponse } from 'utils/hooks/http/proforma.http';
import CURRENCY from 'utils/constants/currency';
import EditClientOrderDetails from '../EditClientOrderDetails/EditClientOrderDetails';
import { IParts } from 'interfaces/price';
import AddReferenceModalWithPrice from 'components/AddReferenceModalWithPrice/AddReferenceModalWithPrice';

type Props = {
  setOpenDetails?
  getClientOrderList?
  orderAction?: string;
  setOrderAction?
};

interface IOrderSummaryTable {
  name: string,
  value: string | number
}

interface IOrderItemTable {
  reference: string,
  quantity: number,
  price: number,
  supplier: string,
  status: string,
  total: number,
  taux: number,
  buy_price: number,
}

interface OrderItem {
  reference: string;
  price: number;
  supplier: string;
  quantity: number;
  status: string;
  total: number;
  id: number;
  codfou: string;
  taux?: number;
  buy_price?: number;
  current_aol_price?: number;

}

const ClientOrderDetails: React.FC<Props> = (props) => {
  const http = useHttp();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [loadingOrderItems, setLoadingOrderItems] = useState(false);
  const [orderSymmary, setOrderSymmary] = useState<Array<{ [key: string]: string | number }>>([]);
  const [orderItems, setOrderItems] = useState<Array<OrderItem>>([]);
  const [orderTotalPrice, setOrderTotalPrice] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [data, setData] = useState<IData>();
  const [hasMore, setHasMore] = useState(false);
  const user = JSON.parse(localStorage.getItem('userData')!);
  const [newRefData, setNewRefData] = useState<IParts>();
  const [isModified, setIsModified] = React.useState(false);
  const [resData, setResData] = useState<IProformaResponse>();
  const [editOrderItems, setEditOrderItems] = useState(false);
  const [updateOrder, setUpdateOrder] = useState(false);
  const [requestGetOrderList, setRequestGetOrderList] = useState(false);
  const [hasSpecialPrice, setHasSpecialPrice] = useState<boolean>(false);
  const [valueDiscount, setValueDiscount] = useState<CodeSupplier[]>([]);
  const [comment, setComment] = useState<string>('');
  const [orderListPage, setOrderListPage] = useState(1);
  const initialized = useRef(false);
  const [, setPageCount] = useState(0);
  const [page, setPage] = useState<number>(searchParams.get('page') ? Number(searchParams.get('page')) : 1);
  const observer = useRef<IntersectionObserver | null>(null);
  const [orderActionRetryStatus, setOrderActionRetryStatus] = useState('');

  const lastItemRef = useCallback(
    (node) => {
      if (loadingOrderItems) { return; }

      if (observer.current) { observer.current.disconnect(); }

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          // Load more data when the last item is in view
          getItems(page + 1);
        }
      });

      if (node) { observer.current.observe(node); }
    },
    [loadingOrderItems, hasMore, page]
  );

  function onConfirmOrder() {
    setLoading(true);

    if (props.orderAction !== 'updated') {
      http.order.orderAction(String(data?.id), { action: (orderActionRetryStatus != '' ? orderActionRetryStatus : props.orderAction) })
        .then((res) => {

          if (res.data?.order && !res.data.order.toString().startsWith('0')) {
            notify(t('_success'), 'success');
          }

          if (props.orderAction == 'confirmed' || orderActionRetryStatus == 'confirmed') {
            setResData(res.data);
            setOpenModal(true);

            if (!res.data?.order || res.data.order.toString().startsWith('0')) {
              setOrderActionRetryStatus('confirmed');
            }

            props.setOrderAction('saveOrder');
          }

          props.getClientOrderList();
        })
        .catch(() => notify(t('_error')))
        .finally(() => {
          setLoading(false);

          if (props.orderAction == 'rejected') {
            props.setOpenDetails(false);
          }

          searchParams.delete('id');
          setSearchParams(searchParams);
        });
    }

    if (props.orderAction === 'updated') {
      setUpdateOrder(true);
    }
  }

  const handleOnCloseModal = () => {
    props.setOpenDetails(false);
    setOpenModal(false);
    setData(undefined);
    setOrderActionRetryStatus('');
    props.setOrderAction('confirmed');
  };

  function onOpenModal() {
    setOpenModal(true);
  }

  const columnsOrderSummary = useMemo(
    (): Column<IOrderSummaryTable>[] => [
      {
        Header: '1',
        columns: [
          {
            Header: '2',
            accessor: 'name',

          },
          {
            Header: '4',
            accessor: 'value',
          },
        ],
      },
    ],
    [
    ],
  );

  const columnsOrderItems = useMemo(
    (): Column<IOrderItemTable>[] => [

      {
        Header: `${t('_reference_submited')}`,
        accessor: 'reference',
      },
      {
        Header: `${t('_mark')}`,
        accessor: 'supplier',
      },
      {
        Header: `${t('_sell_price')}`,
        accessor: 'price',
        Cell: (row) => <span>{CURRENCY[user!.currency]} {row.value}</span>

      },
      {
        Header: `${t('_buy_price')}`,
        accessor: 'buy_price',
        Cell: (row) => <span>{row.value ? CURRENCY[user!.currency] + ' ' + row.value : '-'}</span>

      },
      {
        Header: `${t('_coefficient')}`,
        accessor: 'taux',

      },
      {
        Header: `${t('_qte')}`,
        accessor: 'quantity',

      },
      {
        Header: `${t('_status')}`,
        accessor: 'status',

      },
      {
        Header: 'Total H.T',
        accessor: 'total',
        Cell: (row) => {
          return <div className="total">{CURRENCY[user!.currency]}{row.value.toFixed(2)}</div>;
        }
      },
    ],
    [],
  );

  const getOrderInfo = () => {

    http.order.getOrderById(String(searchParams.get('id'))).then(
      res => {
        setData(res.data);
        setOrderSymmary([
          { name: t('_order_submission'), value: res.data.time },
          { name: t('_confirmation_of_the_order'), value: 'YYYY/MM/DD' },
          // { name: t('_estimated_delivery'), value: res.data.delivery_date ?? 'YYYY/MM/DD' },
          { name: t('_request_of_delivery_date'), value: res.data.delivery_date ?? 'YYYY/MM/DD' },
          { name: t('_proforma_id'), value: res.data.m3_order_id ? String(res.data.m3_order_id) : res.data.proforma_id || '' },
          { name: t('_average_of_the_coefficients'), value: '-' },

        ]);
        setOrderTotalPrice(res.data.total_price);
        setHasSpecialPrice(res.data.is_special_price);
        setValueDiscount(res.data.discount || []);
        setComment(res.data.comment || '');
      })
      .finally(
        () => {
          setLoadingOrder(false);
        }
      );

  };

  useEffect(() => {
    if (initialized.current) {
      return;
    }

    initialized.current = true;

    if (searchParams.get('id')) {
      setLoadingOrder(true);
      getOrderInfo();
      getItems(page);

      const pageFromParams = searchParams.get('order_list_page');

      setOrderListPage(pageFromParams ? Number(pageFromParams) : 1);

      if (pageFromParams) {
        searchParams.delete('order_list_page');
        setSearchParams(searchParams);
      }
    }
  }, []);

  useEffect(() => {
    if (requestGetOrderList) {
      setOrderItems([]);
      setTimeout(() => {
        getOrderInfo();
        getItems(1, true); // prima încărcare => reset
      }, 0);

    }

  }, [requestGetOrderList]);

  useEffect(() => {
    if (orderItems.length > 0 && orderSymmary.length > 0) {

      let averageTaux = 0;

      orderItems.forEach((item) => {
        if (item.taux) {
          averageTaux += Number(item.taux);
        }
      });

      let averageTauxFinal = '-';

      if (averageTaux > 0) {
        averageTauxFinal = (averageTaux / orderItems.length).toFixed(2);
        setOrderSymmary((prev) => {
          const updated = [...prev];

          updated[4].value = averageTauxFinal;

          return updated;
        });
      }


    }
  }, [orderItems, orderSymmary.length]);


  const getItems = (page: number, reset = false) => {
    setLoadingOrderItems(true);

    const params: { page: number; per_page: number; seller_view?: number } = { page: page, per_page: 20000 };

    if (location.pathname.includes('/waiting-confirmation') || location.pathname.includes('/waiting-seller-confirmation')) {
      params.seller_view = 1;
    }


    http.order.getAutomotorOrderItems(searchParams.get('id')!, params)
      .then((res) => {
        setPageCount(res.data.total_pages);


        const uniqueNewItems = res.data.data.map((item) => ({
          id: item.id,
          reference: item.reference,
          quantity: item.quantity,
          price: item.price,
          supplier: item.supplier,
          status: item.status,
          country_restriction: item.country_restriction,
          description: item.description,
          supplier_id: item.supplier_id,
          catalogId: item.catalogId,
          is_manufacturer: item.is_manufacturer,
          codfou: item.codfou,
          total: item.price * item.quantity,
          taux: item.buy_price ? +(item.price / item.buy_price).toFixed(2) : (item.taux ? item.taux : 1),
          buy_price: item.buy_price || 0,
          current_aol_price: item.current_aol_price || 0,
          client_reference: item.client_reference || '',
          buy_current_aol_price: item.buy_current_aol_price || 0,
          old_reference: item.old_reference || '',

        }));

        if (reset) {
          setOrderItems(uniqueNewItems); // înlocuiește complet
        } else {
          setOrderItems([...orderItems, ...uniqueNewItems]); // adaugă la ce era
        }

        setHasMore(page < res.data.total_pages);
        setPage(res.data.page);
      })
      .finally(() => {
        setLoadingOrderItems(false);
        setRequestGetOrderList(false);
      });
  };


  const getModals = () => {


    switch (props.orderAction) {
      case 'confirmed':
      case 'rejected':
      case 'updated':
        return <OrderConfirmationModal orderAction={String(props.orderAction)} setOpenModal={setOpenModal} loading={loading} onConfirmOrder={onConfirmOrder} />;
      case 'saveOrder':
        return <OrderModal data={resData} handleCloseModal={handleOnCloseModal} isDirectorCommercial onConfirmOrder={onConfirmOrder} loading={loading} />;
      case 'addReference':
        return <AddReferenceModalWithPrice tableData={[]} setNewRefData={setNewRefData} setOpenModal={setOpenModal} notSetLocalStorage />;
      default:
        return null;

    }
  };


  const returnStep = () => {
    if (editOrderItems) {
      setEditOrderItems(false);
    } else {
      props.setOpenDetails(false);
      searchParams.delete('id');

      setSearchParams({ ...searchParams, page: String(orderListPage) });

      props.getClientOrderList({ page: orderListPage });
    }
  };

  return (
    <div className="client-order-details-container">
      <div className="back-btn" onClick={() => returnStep()}>
        <FontAwesomeIcon icon={faArrowAltCircleLeft} /> {t('_return')}
      </div>
      <div className="title">
        {t('_summary_of_your_order')}
      </div>
      <div className="order-status">
        <div className="id">
          {t('_order')} <span>#{data?.id}</span>
        </div>
        {!editOrderItems &&
          <div className="status">
            {t('_status')}: <span>{data?.status && t(String(data?.status))}</span>
          </div>
        }
      </div>
      <div className="order-tables">
        <div className="order-tables-confirmation-btns">
          {!editOrderItems ?
            <React.Fragment>
              <DefaultButton text={t('_edit_order')} disabled={loadingOrderItems} onClick={() => { setEditOrderItems(true); }} icon={faEdit} />
              {t(String(data?.status)) !== 'Rejected' && <DefaultButton text={t('_reject')} onClick={() => { onOpenModal(); props.setOrderAction('rejected'); }} red icon={faCancel} />}
              <DefaultButton text={t('_confirm')} onClick={() => { onOpenModal(); props.setOrderAction('confirmed'); }} className="confirmed" icon={faCheckCircle} />
            </React.Fragment>
            :
            <React.Fragment>
              <DefaultButton text={t('_new_reference')} icon={faPlus} onClick={() => { setOpenModal(true); props.setOrderAction('addReference'); }} className="new-reference" />
              <DefaultButton text={t('_update_order')} onClick={() => {
                if (isModified) {
                  setOpenModal(true);
                  props.setOrderAction('updated');
                }
              }} disabled={!isModified} icon={faSave} className="update-order"
              />

            </React.Fragment>

          }
        </div>
        {
          !editOrderItems ?
            <React.Fragment>
              <div className="order-summary">
                <DefaultTable columns={columnsOrderSummary} data={orderSymmary} total={orderTotalPrice} loading={loadingOrder} header={false} />
              </div>
              <div className="automotor-order-items">
                <DefaultTable lastItemRef={lastItemRef} columns={columnsOrderItems} data={orderItems} loading={loadingOrderItems} />
              </div>
            </React.Fragment> :
            <div className="automotor-order-items">
              <EditClientOrderDetails
                data={orderItems}
                orderSymmary={orderSymmary}
                newRefData={newRefData}
                setNewRefData={setNewRefData}
                setIsModified={setIsModified}
                updateOrder={updateOrder}
                setUpdateOrder={setUpdateOrder}
                setLoading={setLoading}
                setOpenModal={setOpenModal}
                setEditOrderItems={setEditOrderItems}
                setRequestGetOrderList={setRequestGetOrderList}
                hasSpecialPrice={hasSpecialPrice}
                valueDiscount={valueDiscount}
                comment={comment}
              />
            </div>

        }


        {/* {pageCount > 1 && <Pagination pageCount={pageCount} forcePage={page} handlePageClick={handlePageClick} />} */}
      </div>

      <Modal
        openModal={openModal}
        shouldCloseOnOverlayClick={false}
        setOpenModal={setOpenModal}
        childrenComponent={getModals()}
      />

    </div >

  );
};

export default ClientOrderDetails;
